import { Spacing } from '@persona-id/design-tokens';
import { produce } from 'immer';
import { pickBy } from 'lodash/fp';
import { useMemo, useState } from 'react';
import styled, { css } from 'styled-components';

import useHasAccessForTierDeterminator from 'dashboard/apps/InquiryTemplateEditor/hooks/useHasAccessForTierDeterminator';
import { EditorAccessTier } from 'dashboard/apps/InquiryTemplateEditor/hooks/useIsEditableWithOptions';
import {
  ComponentAccessTiers,
  ScreenEditorComponentConfigTypes,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/component-constants';
import SectionAccordionCondensed from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentEditorSections/SectionAccordionCondensed';
import {
  COMPONENT_SCHEMA,
  ComponentLibrarySupportedType,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/constants';
import SelfServeComponentBlockWrapper from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ScreenEditorComponentLibrary/SelfServeComponentBlockWrapper';
import {
  ComponentLibraryScreenEditorComponent,
  ComponentLibrarySection,
  isScreenComponentInComponentLibrary,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/types';
import { componentSelfServeUiConditions } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/utils';
import ProductUpsellBanner from 'dashboard/apps/InquiryTemplateEditor/ProductUpsellBanner';
import Divider from 'dashboard/components/Divider';
import SearchInput from 'dashboard/components-lib/SearchInput';
import Spacer from 'design-system/components/Spacer';
import { Paragraph } from 'design-system/components/Text';
import { UiComponentConfig } from 'lib/interfaces/ui-components';

const sidePadding = css`
  padding: 0 ${Spacing[4]};
`;

const ComponentLibraryContainer = styled.div`
  height: 100%;
  margin-bottom: ${Spacing[4]};
`;

const InstructionsText = styled(Paragraph).attrs({
  size: 'sm',
})`
  ${sidePadding};
  margin-bottom: ${Spacing[3]};
  text-align: center;
`;

const BannerWrapper = styled.div`
  margin: 0 ${Spacing[4]} ${Spacing[4]};
`;

const StyledSearchContainer = styled.div`
  ${sidePadding}
  margin-bottom: ${Spacing[2]};
`;

const HeaderLibraryDivider = styled(Divider)`
  margin-top: ${Spacing[4]};
  margin-bottom: ${Spacing[0]};
`;

const SectionsWrapper = styled.div`
  height: 100%;
  overflow-y: scroll;
  // roughly 1 ComponentLibraryBlock height
  padding-bottom: 150px;
`;

const ComponentsWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  grid-gap: ${Spacing[4]} ${Spacing[3]};
  ${sidePadding};
`;

const NoComponentsText = styled(InstructionsText)`
  margin: ${Spacing[3]};
  padding: ${Spacing[4]};
  background-color: ${({ theme }) => theme.color.background.secondary};
`;

const ComponentLibrarySections = Object.fromEntries(
  Object.entries(ComponentLibrarySection)
    .filter(([name, _]) => typeof name !== 'number')
    .map(([_, value]) => [value, []]),
);

type LibraryComponentTuple = [ComponentLibrarySupportedType, ComponentLibraryScreenEditorComponent];

interface LibrarayEditorComponents extends ComponentLibraryScreenEditorComponent {
  type: ComponentLibrarySupportedType;
}

const libraryComponents: LibrarayEditorComponents[] = Object.entries(
  pickBy(isScreenComponentInComponentLibrary, ScreenEditorComponentConfigTypes),
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
).map(([componentType, screenEditorData]: LibraryComponentTuple) => ({
  ...screenEditorData,
  type: componentType,
}));

export const ScreenEditorComponentLibrary = ({ isInitialStep }: { isInitialStep: boolean }) => {
  const hasAccessTier = useHasAccessForTierDeterminator();
  const canUseComponents = hasAccessTier(EditorAccessTier.InquiriesFree);
  const isAccessibleForBasic = hasAccessTier(EditorAccessTier.InquiriesBasic);
  const isAccessibleForEnhanced = hasAccessTier(EditorAccessTier.InquiriesEnhanced);
  const [searchTerm, setSearchTerm] = useState<string>('');

  const componentLibrarySections = useMemo(() => {
    return produce(ComponentLibrarySections, (draft) => {
      libraryComponents
        .sort((a, b) => {
          const nameCompare = a.displayName.localeCompare(b.displayName);
          if (!isAccessibleForEnhanced) {
            // sort by enabled/disabled, then sort each by name
            return (
              Number(hasAccessTier(ComponentAccessTiers[b.type])) -
                Number(hasAccessTier(ComponentAccessTiers[a.type])) || nameCompare
            );
          }
          return nameCompare;
        })
        .filter((component) =>
          searchTerm !== ''
            ? component.displayName.toLowerCase().includes(searchTerm.toLowerCase())
            : true,
        )
        .forEach((screenEditorData) => {
          const { displayName, componentLibrary, featureFlag, type } = screenEditorData;
          const { icon } = componentLibrary;
          const accessTier = ComponentAccessTiers[type];
          const { config } = COMPONENT_SCHEMA[type];

          const { disableImageComponent } = componentSelfServeUiConditions({
            hasFreeTierAccess: canUseComponents,
            componentConfig: config as UiComponentConfig,
            isInitialStep,
            isAccessibleForBasic,
          });

          draft[componentLibrary.section]?.push(
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            <SelfServeComponentBlockWrapper
              key={config.type}
              config={config}
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              featureFlagInfo={featureFlag}
              icon={icon}
              label={displayName}
              disableImageComponentBlock={disableImageComponent}
              disableAllComponents={!canUseComponents}
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              accessTier={accessTier}
            />,
          );
        });
    });
  }, [
    searchTerm,
    isInitialStep,
    canUseComponents,
    isAccessibleForBasic,
    isAccessibleForEnhanced,
    hasAccessTier,
  ]);

  const hasComponentsToDisplay = useMemo(
    () =>
      Object.values(componentLibrarySections).some(
        (sectionComponents) => sectionComponents.length > 0,
      ),
    [componentLibrarySections],
  );

  return (
    <ComponentLibraryContainer>
      <Spacer height={2} />
      {canUseComponents ? (
        <InstructionsText>
          Add components by dragging onto eligible screens (outlined in purple)
        </InstructionsText>
      ) : (
        <BannerWrapper>
          <ProductUpsellBanner
            message='Contact sales to upgrade to the Starter plan and access the component library.'
            learnMoreId='component-library'
            accessTier={EditorAccessTier.InquiriesFree}
          />
        </BannerWrapper>
      )}
      <StyledSearchContainer>
        <SearchInput
          size='sm'
          id='screen-editor-component__search-input'
          title={null}
          value={searchTerm}
          placeholder='Search'
          handleInput={setSearchTerm}
          useClearIcon
        />
      </StyledSearchContainer>
      <HeaderLibraryDivider full />
      <SectionsWrapper>
        {hasComponentsToDisplay ? (
          <>
            {Object.entries(componentLibrarySections)
              .filter(([_, sectionComponents]) => sectionComponents.length > 0)
              .map(([sectionType, sectionComponents]) => (
                <SectionAccordionCondensed
                  key={`section-${sectionType}`}
                  ariaLabel={`${sectionType} components`}
                  title={sectionType}
                  fullWidth
                >
                  <ComponentsWrapper>{sectionComponents}</ComponentsWrapper>
                </SectionAccordionCondensed>
              ))}
          </>
        ) : (
          <NoComponentsText>No components found in search</NoComponentsText>
        )}
      </SectionsWrapper>
    </ComponentLibraryContainer>
  );
};
